import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiService } from '../../../components/Config/api.service';
import LoadingBarWithStyles from '../../../components/Utils/LoadingBarWithStyles';
import Modal from '../../../components/Utils/ModalShow';
import { notify } from '../../../components/Utils/Notifications';

/**
 * Les offres des sociétés (Yros/Kavero#447, contrat C20) : le superadmin Payor
 * choisit, société par société, celles qui ont l'offre RH.
 *
 * Le portail n'invente rien. Il affiche ce que le serveur rend — les sociétés
 * actives de la base, triées par nom — et les range sous leur mère : l'arbre ne
 * sert qu'à la lecture, l'offre ne s'hérite pas.
 */

// Chaque société sous sa mère, dans l'ordre du serveur à chaque niveau. Une
// mère absente de la liste (société archivée) ne fait pas perdre ses filles :
// elles montent à la racine.
function ranger(societes) {
  const presentes = new Set(societes.map(({ id }) => id));
  const filles = new Map();
  const racines = [];
  societes.forEach((societe) => {
    if (presentes.has(societe.mere_id)) {
      filles.set(societe.mere_id, [...(filles.get(societe.mere_id) || []), societe]);
    } else {
      racines.push(societe);
    }
  });

  const lignes = [];
  const descendre = (societe, niveau) => {
    lignes.push({ societe, niveau });
    (filles.get(societe.id) || []).forEach((fille) => descendre(fille, niveau + 1));
  };
  racines.forEach((racine) => descendre(racine, 0));
  return lignes;
}

// L'enveloppe C20 d'un appel : celle du serveur, ou une enveloppe vide — ni
// `ok`, ni `message` — quand il n'y en a pas. Le statut d'abord, jamais la
// présence d'un message : un 502 porte lui aussi un corps, mais dans `error`,
// jamais un refus métier.
async function enveloppeDe(appel) {
  try {
    return (await appel()) || {};
  } catch (erreur) {
    return erreur?.response?.status === 409 ? erreur.response.data || {} : {};
  }
}

// Le `message` du serveur d'abord, mot pour mot ; le repli i18n par `motif`
// ensuite ; le message générique pour un motif hors contrat.
const REPLI_PAR_MOTIF = {
  non_autorise: 'offres.refus.non_autorise',
  valeurs_invalides: 'offres.refus.valeurs_invalides',
  societe_inconnue: 'offres.refus.societe_inconnue',
};
const messageDe = (enveloppe, t, cleGenerique) =>
  enveloppe.message || t(REPLI_PAR_MOTIF[enveloppe.motif] || cleGenerique);

// La confirmation dit ce que le changement coûte, dans les deux sens : rien ne
// part avant qu'elle soit acceptée. Le serveur applique tout de suite ; le menu
// des utilisateurs de la société suit à leur prochain chargement.
function ConfirmationOffre({ societe, activer, envoi, onConfirmer, onAnnuler }) {
  const { t } = useTranslation();
  const sens = activer ? 'activer' : 'couper';
  return (
    <Modal
      isOpen
      onClose={onAnnuler}
      // Une fois la requête partie, fermer la fenêtre ne retient plus rien.
      showCloseButton={!envoi}
      title={t(`offres.${sens}.titre`)}
      footer={
        <>
          <button
            type="button"
            data-testid="offres-annuler"
            onClick={onAnnuler}
            disabled={envoi}
            className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('offres.annuler')}
          </button>
          <button
            type="button"
            data-testid="offres-confirmer"
            onClick={onConfirmer}
            disabled={envoi}
            className={`px-6 py-2 text-white rounded-lg transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
              activer ? 'bg-[--color-primary] hover:bg-[--bg-hover]' : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {envoi ? t('offres.envoi') : t(`offres.${sens}.confirmer`)}
          </button>
        </>
      }
    >
      <p data-testid="offres-confirmation" className="text-sm text-gray-700">
        {t(`offres.${sens}.message`, { nom: societe.nom })}
      </p>
    </Modal>
  );
}

function Offres({ societeActiveId }) {
  const { t, i18n } = useTranslation();
  const [societes, setSocietes] = useState(null);
  const [echec, setEchec] = useState(null);
  // La société dont l'offre va changer, et le sens du changement, tant que le
  // superadmin n'a pas confirmé : l'interrupteur ne bouge qu'avec la réponse.
  const [aConfirmer, setAConfirmer] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    (async () => {
      const lecture = await enveloppeDe(() => apiService.lireOffres());
      if (lecture.ok) setSocietes(lecture.societes);
      else setEchec(lecture);
    })();
  }, []);

  const langue = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'fr-FR';
  // « Modifiée le … par … », ou un tiret sans trace. La clé étrangère de
  // l'auteur laisse la date sans auteur quand le compte a été supprimé.
  const trace = (societe) => {
    if (!societe.modifiee_le) return t('offres.sansTrace');
    const date = new Intl.DateTimeFormat(langue, { dateStyle: 'short', timeStyle: 'short' }).format(
      new Date(societe.modifiee_le)
    );
    return societe.modifiee_par
      ? t('offres.trace', { date, auteur: societe.modifiee_par })
      : t('offres.traceSansAuteur', { date });
  };

  const confirmer = async () => {
    const { societe, activer } = aConfirmer;
    setEnvoi(true);
    const reponse = await enveloppeDe(() => apiService.changerOffre(societe.id, activer));
    setEnvoi(false);
    setAConfirmer(null);
    if (!reponse.ok) {
      notify(messageDe(reponse, t, 'offres.erreurGenerique'), '', 3);
      return;
    }
    // La ligne prend la réponse du serveur, à sa place dans l'arbre.
    setSocietes((courantes) =>
      courantes.map((courante) => (courante.id === reponse.societe.id ? reponse.societe : courante))
    );
    // Le menu suit l'offre de la société active : le portail se recharge, une
    // fois le changement confirmé par le serveur, pas avant.
    if (societe.id === societeActiveId) window.location.reload();
  };

  if (echec) {
    return (
      <p
        data-testid="offres-erreur"
        className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
      >
        {messageDe(echec, t, 'offres.erreurChargement')}
      </p>
    );
  }
  if (!societes) return <LoadingBarWithStyles />;

  return (
    <>
      <ul
        data-testid="offres-liste"
        className="bg-white rounded-2xl shadow-sm border border-slate-100 divide-y divide-slate-100"
      >
        {ranger(societes).map(({ societe, niveau }) => (
          <li
            key={societe.id}
            data-testid={`societe-${societe.id}`}
            aria-level={niveau + 1}
            className="flex items-center gap-4 py-3 pr-4"
            style={{ paddingLeft: `${1 + niveau * 1.5}rem` }}
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">{societe.nom}</p>
              <p data-testid="societe-trace" className="text-xs text-slate-500">
                {trace(societe)}
              </p>
            </div>
            <span className="text-sm text-slate-600">
              {societe.offre_rh ? t('offres.paieRh') : t('offres.paie')}
            </span>
            {/* Contrôlée : la case ne bascule qu'avec la réponse du serveur. */}
            <input
              type="checkbox"
              role="switch"
              className="toggle toggle-sm toggle-success"
              checked={societe.offre_rh}
              onChange={() => setAConfirmer({ societe, activer: !societe.offre_rh })}
              aria-label={t('offres.interrupteur', { nom: societe.nom })}
            />
          </li>
        ))}
      </ul>

      {aConfirmer && (
        <ConfirmationOffre
          {...aConfirmer}
          envoi={envoi}
          onConfirmer={confirmer}
          onAnnuler={() => setAConfirmer(null)}
        />
      )}
    </>
  );
}

export default Offres;
